import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@Component({
  selector: 'app-score-cards',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatProgressBarModule
  ],
  templateUrl: './score-cards.component.html',
  styleUrls: ['./score-cards.component.scss']
})
export class ScoreCardsComponent implements OnInit, OnChanges {
  @Input() universities: any[] = [];
  @Input() metrics: string[] = [];
  @Input() title: string = 'University Score Cards';
  @Input() maxEntities: number|undefined;

  processedUniversities: any[] = [];
  selectedMetrics: string[] = [];

  ngOnInit(): void {
    this.processData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['universities'] || changes['metrics']) {
      this.processData();
    }
  }

  private processData(): void {
    if (!this.universities || !this.metrics) return;

    // Limit universities to prevent clutter
    const limitedUniversities = this.universities.slice(0, this.maxEntities ?? this.universities.length);

    // Select top 6 most important metrics to avoid clutter
    this.selectedMetrics = this.metrics.slice(0, 6);

    // Process university data
    this.processedUniversities = limitedUniversities.map(uni => {
      const universityData: any = {
        name: uni.name || uni.university,
        overallScore: 0,
        metrics: {}
      };

      // Calculate metrics and overall score
      let totalScore = 0;
      let metricCount = 0;

      this.selectedMetrics.forEach(metric => {
        const metricValue = uni[metric] ||
          uni.series?.find((s: any) => s.name === metric)?.value ||
          0;
        const numericValue = typeof metricValue === 'number' ? metricValue : parseFloat(metricValue) || 0;

        universityData.metrics[metric] = {
          value: numericValue,
          color: this.getScoreColor(numericValue),
          icon: this.getScoreIcon(numericValue)
        };

        totalScore += numericValue;
        metricCount++;
      });

      universityData.overallScore = metricCount > 0 ? Math.round(totalScore / metricCount) : 0;
      universityData.overallColor = this.getScoreColor(universityData.overallScore);
      universityData.overallIcon = this.getScoreIcon(universityData.overallScore);

      return universityData;
    });

    // Sort by overall score descending
    this.processedUniversities.sort((a, b) => b.overallScore - a.overallScore);
  }

  getScoreColor(score: number): string {
    if (score >= 80) return '#4caf50'; // Green
    if (score >= 60) return '#ff9800'; // Orange
    return '#f44336'; // Red
  }

  getScoreIcon(score: number): string {
    if (score >= 80) return 'star';
    if (score >= 60) return 'check_circle';
    return 'warning';
  }

  getScoreLabel(score: number): string {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    return 'Needs Improvement';
  }

  getMetricDisplayName(metric: string): string {
    return metric.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  getAverageScore(): string {
    if (this.processedUniversities.length === 0) return '0';
    const total = this.processedUniversities.reduce((sum, uni) => sum + uni.overallScore, 0);
    return (total / this.processedUniversities.length).toFixed(1);
  }

  getScoreRange(): string {
    if (this.processedUniversities.length === 0) return '0 - 0';
    const min = this.processedUniversities[this.processedUniversities.length - 1].overallScore;
    const max = this.processedUniversities[0].overallScore;
    return `${min} - ${max}`;
  }
}
